/**************************************************
 * components/dash/CurrentGrid.js
 *
 * @author  LSJ
 * @since   1.0 (2025-02-28)
 * @version 1.0
 **************************************************/
import React, { useMemo } from "react";
import { useDragScroll } from "@hooks";
import "./Dashboard.css";
import dangerImg from "./images/danger.png";
import { filterRoleList } from "@utils/dashboardApi";
import safeImg from "./images/safe.png";

/**
 * CurrentGrid
 *
 * "프레젠테이션 컴포넌트"로서, [중대재해 발생 현장] 대시보드 그리드를 렌더링합니다.
 * 전달받은 props를 이용하여 [테이블 헤더, 본문, 푸터], 그리고 셀 병합(rowSpan) 등 처리를 수행합니다.
 *
 * @param {String}  compName    회사 정보: 회사명
 * @param {String}  supervisor  회사 정보: 관리감독자
 * @param {Array}   data        중대재해 발생 현장 데이터 (CurrentDTO 리스트)
 */
const CurrentGrid = ({ siteCodes, roleList, companyInfo, data, selectedSite = "", managerName, managerPhone }) => {
  // useDragScroll 훅을 사용하여 테이블 영역의 드래그-스크롤 기능 구현
  const { containerRef, eventHandlers, cursorStyle } = useDragScroll();

  // 근로자 대표 찾기 (역할 코드로 검색)
  const laborRepCode = roleList?.find(role => role.comdName === "근로자 대표")?.comdCode;

  // 사이트 옵션
  const siteOptions = useMemo(() => {
    const options = [{ key: '', value: '전체 사업장' }];

    if (siteCodes && siteCodes.length > 0) {
      const siteOptions = siteCodes.map(site => ({
        key: site.siteCode?.toString() || 'null',
        value: site.siteName || '미지정'
      }));

      // 정렬: 숫자 코드 → 오름차순
      siteOptions.sort((a, b) => {
        const na = Number(a.key), nb = Number(b.key);
        const ia = Number.isNaN(na), ib = Number.isNaN(nb);
        if (ia && ib) return a.value.localeCompare(b.value);
        if (ia) return 1;
        if (ib) return -1;
        return na - nb;
      });

      options.push(...siteOptions);
    }

    return options;
  }, [siteCodes]);

  const effectiveSelected = selectedSite || siteOptions[0]?.key || '';

  // 렌더링용 데이터: 사업장이 선택되면 해당 사업장만 표시
  const filteredData = useMemo(() => {
    if (!effectiveSelected || effectiveSelected === '') return data;

    const selectedSiteCode = Number(effectiveSelected);
    return data.filter((d) => {
      return d.siteCode === selectedSiteCode;
    });
  }, [data, effectiveSelected]);

  // ── 직무 필터링/역할 (선택된 사업장의 역할 정보)
  const { filteredRoleEmpInfo } = filterRoleList(roleList, companyInfo);

  const roleEmpInfoBySite = useMemo(() => {
    if (!effectiveSelected || effectiveSelected === '') return filteredRoleEmpInfo;

    const siteCode = Number(effectiveSelected);
    return filteredRoleEmpInfo.filter(r => {
      const rCode = r.siteCode != null ? Number(r.siteCode) : null;
      return rCode === siteCode;
    });
  }, [filteredRoleEmpInfo, effectiveSelected]);

  // 선택된 사업장에 따른 근로자 대표 찾기
  const laborRep = useMemo(() => {
    if (!laborRepCode) return null;
    
    // 특정 사업장이 선택된 경우, 해당 사업장의 근로자 대표 찾기
    if (selectedSite && selectedSite !== '') {
      return roleEmpInfoBySite.find(emp => emp.role === laborRepCode);
    }
    
    // 전체 사업장인 경우, companyInfo에서 찾기
    return companyInfo?.empInfoList?.find(emp => emp.role === laborRepCode);
  }, [laborRepCode, selectedSite, roleEmpInfoBySite, companyInfo?.empInfoList]);

  // 동적 컬럼 개수
  const dynamicColumnCount = {
    dataTotalCnt: filteredData.length,
  };

  // 데이터가 없으면 사용자에게 메시지 표시
  if (!data || data.length === 0) {
    return <div>데이터가 없습니다.</div>;
  }

  // 전체 사업장이 선택되었거나 데이터가 없으면 메시지 표시
  if (filteredData.length === 0 || !effectiveSelected || effectiveSelected === '') {
    const message = !effectiveSelected || effectiveSelected === '' 
      ? "사업장을 선택하면 중대재해 발생 현황을 확인할 수 있습니다."
      : "선택한 사업장에 표시할 데이터가 없습니다.";
      
    return (
        <div className="text-center py-4">{message}</div>
    );
  }

  return (
      <div className="cg-wrap">
        {/* 제목 */}
        <h2 className="cg-title">[사업장별 중대재해 불확신등급 공정]</h2>

        {/* 요약 카드 */}
        <div className="cg-summary-card">
          <div className="cg-summary-row">
            <div className="cg-summary-item">
              <span className="cg-summary-label">사업장</span>
              <span className="cg-summary-value">{(() => {
                const foundSite = siteCodes?.find(site => site.siteCode?.toString() === selectedSite?.toString());
                return foundSite?.siteName || selectedSite;
              })()}</span>
            </div>
            <div className="cg-summary-item">
              <span className="cg-summary-label">근로자 대표</span>
              <span className="cg-summary-value">{laborRep?.empName ?? "-"}</span>
            </div>
          </div>
        </div>

        {/* 공정 카드 목록 */}
        <div className="cg-cards-wrap" ref={containerRef} {...eventHandlers} style={{ cursor: cursorStyle }}>
          {filteredData.map((item) => (
            <div
              key={`card-${item.siteCode ?? "NULL"}-${item.procCode}`}
              className={`cg-proc-card${item.hasAccident ? " cg-proc-card--danger" : ""}`}
            >
              {/* 카드 헤더: 공정명 */}
              <div className="cg-proc-card-header">
                <span className="cg-proc-name">{item.procName}</span>
              </div>

              {/* 카드 본문: 발생형태, 설비 */}
              <div className="cg-proc-card-body">
                <div className="cg-proc-row">
                  <span className="cg-proc-row-label">설비</span>
                  <div className="cg-proc-row-value" style={{ color: 'var(--dash-accent)' }}>
                    {item.eqptNames && item.eqptNames.map((name, index) => <div key={index}>{name}</div>)}
                  </div>
                </div>
                <div className="cg-proc-row" style={{ flexDirection: 'column' }}>
                  <div className="cg-proc-row-value" style={item.hasAccident ? { color: 'var(--dash-danger)' } : { color: 'var(--dash-text-primary)' }}>
                    {item.saccNames && item.saccNames?.length > 0
                      ? item.saccNames?.map((name, index) => <div key={index}>{name}</div>)
                      : <div>N/A</div>}
                  </div>
                </div>
              </div>

              {/* 카드 푸터: 확신여부 아이콘 + 배지 */}
              <div className="cg-proc-card-footer">
                {item.hasAccident ? (
                  <>
                    <img className="img" src={dangerImg} alt="불확신" />
                    <span className="cg-proc-badge cg-proc-badge--danger">
                      {item.saccNames && item.saccNames.length >= 1 ? "Risk" : "Caution"}
                    </span>
                  </>
                ) : (
                  <>
                    <img className="img" src={safeImg} alt="확신" />
                    <span className="cg-proc-badge cg-proc-badge--safe">Safe</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 푸터 */}
        <div className="cg-footer-row">
          <div className="cg-footer-legend">
            <p className="m-0">
              주1) 범례: <span style={{ color: 'var(--dash-danger)' }}>■</span> 중대재해 발생 현장
            </p>
            <p className="m-0">주2) 중대재해: 중처법 기준(사망 1인 이상, 동일 원인 2인 이상 6개월 이상 휴업 부상, 시민재해 10명 이상)</p>
          </div>
          {(managerName || managerPhone) && (
            <div className="cg-footer-manager">
              <span className="cg-manager-label">시스템 담당자 정보</span>
              <div>👤 담당자: {managerName}</div>
              <div>📞 연락처: {managerPhone}</div>
            </div>
          )}
        </div>
      </div>
  );
};

export default CurrentGrid;